import { FC, useEffect, useState } from 'react'
import { Box, Link, Paper, IconButton } from '@mui/material'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { useMessage, useCurrency } from '~/src/hooks'
import { useUsers } from '~/src/hooks/useApi'
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import TextareaAutosize from '@mui/material/TextareaAutosize'
import { InputDialog } from '~/src/components/organisms/InputDialog/InputDialog'
import { useNavigate } from 'react-router-dom'
import { IContainerResponse } from '~/src/repositories/Container/schema/Container'
import { ContainerOrderFileUpload } from '~/src/components/pages/Order/ContainerOrder/ContainerOrderDetail/components/ContainerOrderFile/ContainerOrderFileUpload/ContainerOrderFileUpload'
import { formatMoney } from '~/src/utils/formatMoney'
import { stringToNumber } from '~/src/libs'

interface ContainerOrderFileProps {
  containerId: number
  data?: IContainerResponse['data']['containerFilesInfo']
  patchMemo: (param: string) => void
  patchBalance: (param: number) => void
  containerUserId: number
}

export const ContainerOrderFile: FC<ContainerOrderFileProps> = ({
  containerId,
  data,
  patchMemo,
  patchBalance,
  containerUserId,
}): JSX.Element => {
  const [dialogTitle, setDialogTitle] = useState<string>('')
  const [inputDialogToggleState, setInputDialogToggleState] = useState<boolean>(false)
  const [formCategory, setFormCategory] = useState<string>('')
  const [inputError, setInputError] = useState<boolean>(false)
  const [moneyValue, setMoneyValue] = useState<string>('')
  const [tempValue, setTempValue] = useState<string>('')
  const { handleOpenWithErrorMessage } = useMessage()
  const { error: usersError } = useUsers({ limit: 1000000 })
  const { baseCurrencyUnit, unitPosition } = useCurrency()
  const { t } = useTranslation()
  const navigate = useNavigate()

  useEffect(() => {
    if (usersError) {
      handleOpenWithErrorMessage(usersError)
    }
  }, [usersError])

  /**
   * handleSetMoneyValue
   */
  const handleSetMoneyValue = (): boolean | void => {
    if (new RegExp(/[^0-9]/).test(tempValue)) {
      setInputError(true)
      return false
    }
    setInputError(false)
    if (tempValue.length === 0) {
      setMoneyValue('0')
    } else {
      patchBalance(parseInt(tempValue))
      setMoneyValue(parseInt(tempValue).toLocaleString())
    }
    setInputDialogToggleState(false)
  }

  /**
   * handleSetValue
   * Input Dialogからsubmitされた処理
   */
  const handleSetValue = (): void => {
    if (formCategory === 'number') {
      handleSetMoneyValue()
    }
  }

  /**
   * handleMoneyDialogToggle
   */
  const handleMoneyDialogToggle = (): void => {
    setTempValue(moneyValue.replace(/,/g, ''))
    setInputDialogToggleState(!inputDialogToggleState)
    setDialogTitle(t('container_order_detail.enter_the_remaining_balance'))
    setFormCategory('number')
  }

  /**
   *  handleClickDialogCancel
   *  残金入力ダイアログ内のキャンセルボタンを押した時の処理
   */
  const handleClickDialogCancel = () => {
    setTempValue('')
  }

  return (
    <Paper>
      <Typography
        paddingTop={'16px'}
        paddingLeft={'16px'}
        borderBottom={'1px solid'}
        borderColor={'rgba(0, 0, 0, 0.12)'}
        variant="h2"
        paddingBottom={'16px'}
        component="div"
        fontSize={'20px'}
        fontFamily={'Roboto'}
        fontWeight={'Medium'}
        lineHeight={'32px'}
        color={'black'}
      >
        {`${t('container_order_detail.file')}`}
      </Typography>
      <Box padding={'32px 16px 32px 32px'}>
        <Box display={'flex'} alignItems={'center'} padding={'16px 0'}>
          <Typography
            minWidth={'180px'}
            fontWeight={'Regular'}
            fontFamily={'Roboto'}
            fontSize={'14px'}
            lineHeight={'14px'}
            color={'black'}
          >
            {`${t('container_order_detail.statement_of_account')}`}
          </Typography>
          <Link
            fontSize={'14px'}
            lineHeight={'14px'}
            fontFamily={'Roboto'}
            color={'#3F51B5'}
            paddingLeft={'32px'}
            fontWeight={'Regular'}
            underline={'always'}
            onClick={() =>
              navigate('/admin/order/statement-of-account/list', { state: { id: containerUserId } })
            }
          >
            {`${t('container_order_detail.link_to_statement_of_account')}`}
          </Link>
        </Box>
        <Box display={'flex'} alignItems={'center'} padding={'16px 0'}>
          <Typography
            minWidth={'180px'}
            fontWeight={'Regular'}
            fontFamily={'Roboto'}
            fontSize={'14px'}
            lineHeight={'14px'}
            color={'black'}
          >
            {`${t('container_order_detail.balance')}`}
          </Typography>
          <Box
            alignItems={'center'}
            justifyContent={'center'}
            marginLeft={'32px'}
            display={'flex'}
            flexDirection={'row'}
          >
            <Typography
              fontWeight={'Regular'}
              fontFamily={'Roboto'}
              fontSize={'16px'}
              lineHeight={'24px'}
              color={'black'}
            >
              {formatMoney(stringToNumber(moneyValue === '' && data?.balance
                ? data.balance
                : moneyValue), baseCurrencyUnit, unitPosition)}
            </Typography>
            <IconButton
              color="primary"
              onClick={handleMoneyDialogToggle}
              sx={{
                border: '1px solid rgba(63, 81, 181, 0.5)',
                boxSizing: 'border-box',
                borderRadius: '4px',
                marginLeft: '8px',
                width: '36px',
                height: '36px',
              }}
            >
              <DriveFileRenameOutlineOutlinedIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          </Box>
        </Box>

        <ContainerOrderFileUpload
          containerId={containerId}
          label={t('container_order_detail.packing_list')}
          fileType={'PACKING_LIST'}
          fileMeta={data?.packingList ? data.packingList : undefined}
        />

        <Box display={'flex'} alignItems={'center'} padding={'16px 0'}>
          <Box>
            <Typography
              minWidth={'180px'}
              fontWeight={'Regular'}
              fontFamily={'Roboto'}
              fontSize={'14px'}
              lineHeight={'14px'}
              color={'black'}
            >
              {`${t('container_order_detail.proforma_invoice')}`}
            </Typography>
            <Typography
              minWidth={'180px'}
              fontWeight={'Regular'}
              fontFamily={'Roboto'}
              fontSize={'14px'}
              lineHeight={'14px'}
              color={'black'}
            >
              {`${t('container_order_detail.sales_invoice')}`}
            </Typography>
          </Box>
          <Link
            fontSize={'14px'}
            lineHeight={'14px'}
            fontFamily={'Roboto'}
            color={'#3F51B5'}
            paddingLeft={'32px'}
            fontWeight={'Regular'}
            underline="always"
            onClick={() =>
              navigate('/admin/order/proforma-invoice/list', { state: { id: containerUserId } })
            }
          >
            {`${t('container_order_detail.link_to_issue_invoice')}`}
          </Link>
        </Box>

        <ContainerOrderFileUpload
          containerId={containerId}
          label={t('container_order_detail.commercial_invoice')}
          fileType={'BILL'}
          fileMeta={data?.bill ? data.bill : undefined}
        />

        <ContainerOrderFileUpload
          containerId={containerId}
          label={t('container_order_detail.surrender_b/l')}
          fileType={'SURRENDER_BL'}
          fileMeta={data?.surrenderBl ? data.surrenderBl : undefined}
        />

        <ContainerOrderFileUpload
          containerId={containerId}
          label={t('container_order_detail.b/l_draft')}
          fileType={'BL_DRAFT'}
          fileMeta={data?.blDraft ? data.blDraft : undefined}
        />

        <Box display={'flex'} padding={'16px 0'}>
          <Typography
            minWidth={'180px'}
            fontWeight={'Regular'}
            fontFamily={'Roboto'}
            fontSize={'14px'}
            lineHeight={'14px'}
            color={'black'}
          >
            {`${t('container_order_detail.note')}`}
          </Typography>
          <Box marginLeft={'32px'}>
            <TextareaAutosize
              aria-label="minimum height"
              minRows={3}
              placeholder={t('container_order_detail.comment')}
              style={{ width: 306, height: 180, padding: '10px' }}
              onBlur={(e) => patchMemo(e.target.value)}
              defaultValue={data?.remark}
            />
          </Box>
        </Box>

        <ContainerOrderFileUpload
          containerId={containerId}
          label={t('container_order_detail.file_name')}
          fileType={'OTHER'}
          fileMeta={data?.others && data.others.length > 0 ? data.others[0] : undefined}
          displayOrder={1}
        />
        <ContainerOrderFileUpload
          containerId={containerId}
          label={t('container_order_detail.file_name')}
          fileType={'OTHER'}
          fileMeta={data?.others && data.others.length >= 1 ? data.others[1] : undefined}
          displayOrder={2}
        />
      </Box>
      <InputDialog
        inputError={inputError}
        tempValue={tempValue}
        dialogToggleState={inputDialogToggleState}
        setTempValue={setTempValue}
        setDialogToggleState={setInputDialogToggleState}
        handleSetValue={handleSetValue}
        handleClickDialogCancel={handleClickDialogCancel}
        dialogTitle={dialogTitle}
        formCategory={formCategory}
      />
    </Paper>
  )
}
